import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_API_URL || 'https://compliance.kurukshetraanalytics.com/backend/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ct_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Set JSON Content-Type only for non-FormData requests.
  // For FormData, leave it unset so axios auto-sets multipart/form-data with the boundary.
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('ct_token');
      localStorage.removeItem('ct_admin');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

export const authAPI = {
  login:  (email, password) => api.post('/auth/login.php', { email, password }),
  logout: ()                => api.post('/auth/logout.php'),
};

export const employeesAPI = {
  list:             (params)   => api.get('/employees/list.php', { params }),
  get:              (id)       => api.get('/employees/get.php', { params: { id } }),
  create:           (data)     => api.post('/employees/create.php', data),
  update:           (data)     => api.post('/employees/update.php', data),
  delete:           (id)       => api.post('/employees/delete.php', { id }),
  visaHistory:      (id)       => api.get('/employees/visa_history.php', { params: { employee_id: id } }),
  // Generic single-step visa transition (all paths except OPT → STEM_OPT).
  // Pass a FormData object when transitioning to H1B (supports optional i797_file).
  transition:        (data) => api.post('/employees/transition.php', data),
  revertTransition:  (data) => api.post('/employees/revert_transition.php', data),
  // Two-step STEM OPT transition
  applyStem:    (data)     => api.post('/employees/apply_stem.php', data),
  approveStem:  (formData) => api.post('/employees/approve_stem.php', formData),
  updateStem:   (formData) => api.post('/employees/update_stem.php', formData),
  stemFileUrl: (employeeId) =>
    `${BASE_URL}/employees/preview_stem_file.php?employee_id=${employeeId}&token=${localStorage.getItem('ct_token')}`,
};

export const i983API = {
  list:   (employeeId) => api.get('/i983/list.php', { params: { employee_id: employeeId } }),
  upload: (formData)   => api.post('/i983/upload.php', formData),
  delete: (id)         => api.post('/i983/delete.php', { id }),
  update: (data)       => api.post('/i983/update.php', data),
  previewUrl:       (doc) => `${BASE_URL}/i983/preview.php?id=${doc.id}&token=${localStorage.getItem('ct_token')}`,
  offerLetterUrl:   (doc) => `${BASE_URL}/i983/preview.php?id=${doc.id}&type=offer_letter&token=${localStorage.getItem('ct_token')}`,
};

export const documentsAPI = {
  list:        (employeeId) => api.get('/documents/list.php', { params: { employee_id: employeeId } }),
  upload:      (formData)   => api.post('/documents/upload.php', formData),
  delete:        (id)   => api.post('/documents/delete.php', { id }),
  updateDetails: (data) => api.post('/documents/update_details.php', data),
  previewUrl:  (docId)      => `${BASE_URL}/documents/preview.php?id=${docId}&token=${localStorage.getItem('ct_token')}`,
  downloadUrl: (docId)      => `${BASE_URL}/documents/download.php?id=${docId}&token=${localStorage.getItem('ct_token')}`,
};

export const timelineAPI = {
  list:   (employeeId) => api.get('/timeline/list.php', { params: { employee_id: employeeId } }),
  save:   (data)       => api.post('/timeline/save.php', data),
  delete: (id)         => api.post('/timeline/delete.php', { id }),
};

export const reportsAPI = {
  list:       (employeeId) => api.get('/reports/list.php', { params: { employee_id: employeeId } }),
  save:       (formData)   => api.post('/reports/save.php', formData),
  previewUrl: (reportId)   => `${BASE_URL}/reports/preview.php?id=${reportId}&token=${localStorage.getItem('ct_token')}`,
};

export const gapsAPI = {
  list: (employeeId) => api.get('/gaps/list.php', { params: { employee_id: employeeId } }),
  save: (data)       => api.post('/gaps/save.php', data),
};

export const alertsAPI    = { list: () => api.get('/alerts/list.php') };
export const auditAPI     = { list: () => api.get('/audit/list.php') };
export const dashboardAPI = { stats: () => api.get('/dashboard/stats.php') };
export const logsAPI      = { list: (params) => api.get('/logs/list.php', { params }) };

export const candidatesAPI = {
  list:             (params) => api.get('/candidates/list.php', { params }),
  get:              (id)     => api.get('/candidates/get.php', { params: { id } }),
  create:           (data)   => api.post('/candidates/create.php', data),
  update:           (data)   => api.post('/candidates/update.php', data),
  sendDocLink:      (data)   => api.post('/candidates/send_doc_link.php', data),
  documentsList:    (id)     => api.get('/candidates/documents_list.php', { params: { candidate_id: id } }),
  verifyDocument:   (data)   => api.post('/candidates/verify_document.php', data),
  deleteDocument:   (id)     => api.post('/candidates/delete_document.php', { document_id: id }),
  adminUploadDoc:   (fd)     => api.post('/candidates/admin_upload_document.php', fd),
  requestReupload:  (data)   => api.post('/candidates/request_reupload.php', data),
  adminReview:      (data)   => api.post('/candidates/admin_review.php', data),
  authorizeOffer:   (data)   => api.post('/candidates/authorize_offer.php', data),
  sendOffer:        (data)   => api.post('/candidates/send_offer.php', data),
  offerResponse:    (data)   => api.post('/candidates/offer_response.php', data),
  scheduleOnboarding:     (data) => api.post('/candidates/schedule_onboarding.php', data),
  rescheduleOnboarding:   (data) => api.post('/candidates/reschedule_onboarding.php', data),
  revertStage:            (data) => api.post('/candidates/revert_stage.php', data),
  convertToEmployee:      (data) => api.post('/candidates/convert_to_employee.php', data),
  resendOnboardingInvite: (data) => api.post('/candidates/resend_onboarding_invite.php', data),
  completeOnboarding:     (data) => api.post('/candidates/complete_onboarding.php', data),
  pipelineReport:   ()       => api.get('/candidates/pipeline_report.php'),
  previewDocUrl:    (id)     => `${BASE_URL}/candidates/preview_document.php?id=${id}&token=${localStorage.getItem('ct_token')}`,
};

export const screeningAPI = {
  save: (data)        => api.post('/screening/save.php', data),
  get:  (candidateId) => api.get('/screening/get.php', { params: { candidate_id: candidateId } }),
};

export const i9API = {
  sendLink:            (data) => api.post('/i9/send_link.php', data),
  list:                (params) => api.get('/i9/list.php', { params }),
  // Admin uploads the completed I-9 (FormData: i9_id + completed_i9_file)
  uploadCompleted:     (fd)   => api.post('/i9/hr_complete.php', fd),
  // Admin approves (FormData: i9_id, everify_done, optional everify_file)
  adminApprove:        (fd)   => api.post('/i9/admin_approve.php', fd),
  // Preview URLs for I-9 files (candidate | completed | everify)
  previewUrl: (i9Id, fileType = 'candidate') =>
    `${BASE_URL}/i9/preview.php?i9_id=${i9Id}&file_type=${fileType}&token=${localStorage.getItem('ct_token')}`,
};

export const syncAPI = {
  pushEmployee: (data) => api.post('/sync/push_employee.php', data),
  bulkPush:     ()     => api.post('/sync/bulk_push.php', {}),
  status:       ()     => api.get('/sync/status.php'),
};

export const expiryConfigAPI = {
  list:   ()     => api.get('/expiry/config.php'),
  update: (data) => api.post('/expiry/config.php', data),
};
